import React, { useEffect, useState } from "react";
import { Button, Modal, Select, Form, Checkbox, DatePicker } from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormField from "./FormField";
import {
  notificationsSchema,
  notificationsSchemaWithData,
} from "constants/yupSchema";
import { DevTool } from "@hookform/devtools";
import SecondaryButton from "modules/SubComponents/Buttons/SecondaryButton";
import PrimaryButton from "modules/SubComponents/Buttons/PrimaryButton";
import {
  cancelNotifications,
  editNotifications,
  sendNotifications,
} from "Network";
import { toastError, toastSuccess } from "helpers/toasters";
import moment from "moment";

const { Option } = Select;

export default function NotificationModal({
  open,
  handleCancel,
  selectedNotification,
  mode,
  setFilters,
}) {
  const [loading, setLoading] = useState(false);
  const [withDate, setWithDate] = useState(true);
  const [checked, setChecked] = useState(true);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    getValues,
    setValue,
    watch,
  } = useForm({
    mode: "all",
    resolver: yupResolver(
      withDate ? notificationsSchemaWithData : notificationsSchema
    ),
    defaultValues: {
      titleEn: "",
      titleAr: "",
      messageEn: "",
      messageAr: "",
      userType: null,
      scheduleTime: "",
      isSendNow: false,
    },
  });

  useEffect(() => {
    if (selectedNotification) {
      console.log("selectedNotification", selectedNotification);
      let userType = +selectedNotification?.Recipients;
      if (mode === "View" || mode === "Edit") {
        //1:allUsers , 2:subscribed,3:unsubscribed
        userType =
          selectedNotification?.Recipients === 1
            ? "All users"
            : selectedNotification?.Recipients === 2
            ? "Subscribed users"
            : selectedNotification?.Recipients === 3
            ? "Unsubscribed users"
            : "";
      }
      console.log("userType", userType);
      reset({
        titleEn: selectedNotification?.titleEn,
        titleAr: selectedNotification?.titleAr,
        messageEn: selectedNotification?.messageEn,
        messageAr: selectedNotification?.messageAr,
        userType,
        scheduleTime: moment.utc(selectedNotification?.scheduleTime),
        isSendNow: false,
      });
    } else {
      reset({
        titleEn: "",
        titleAr: "",
        messageEn: "",
        messageAr: "",
        userType: null,
        scheduleTime: "",
        isSendNow: false,
      });
    }
  }, [selectedNotification]);

  useEffect(() => {
    if (getValues("isSendNow")) {
      setWithDate(false);
    } else {
      setWithDate(true);
      setValue("scheduleTime", "");
    }
  }, [getValues("isSendNow"), getValues, checked]);

  const onCancel = () => {
    handleCancel();
    reset();
  };

  const handleSuccess = (success) => {
    toastSuccess(success?.message);
    onCancel();
    setLoading(false);
    setFilters({});
  };

  const handleError = (error) => {
    toastError(error?.message);
    setLoading(false);
  };

  const onSubmit = (data) => {
    console.log("🚀 ~ data:", data);
    let userType = +data?.userType;
    if (selectedNotification) {
      userType =
        getValues("userType") === "All users"
          ? 1
          : getValues("userType") === "Subscribed users"
          ? 2
          : getValues("userType") === "Unsubscribed users"
          ? 3
          : null;
    }
    const payload = {
      "title.ar": data?.titleAr,
      "title.en": data?.titleEn,
      "message.ar": data?.messageAr,
      "message.en": data?.messageEn,
      userType:
        selectedNotification && typeof userType === "number"
          ? userType
          : +data?.userType, //1:allUsers , 2:subscribed,3:unsubscribed
      scheduleTime: data.scheduleTime
        ? moment(data.scheduleTime).toISOString()
        : "",
      isSendNow: data?.isSendNow,
    };

    console.log("payload", payload);

    setLoading(true);
    if (mode === "Send") {
      return sendNotifications(
        payload,
        (success) => {
          handleSuccess(success?.message);
        },
        (error) => {
          handleError(error?.message);
        }
      );
    }
    editNotifications(
      { ...payload, notificationId: selectedNotification?._id },
      (success) => {
        handleSuccess(success?.message);
      },
      (error) => {
        handleError(error?.message);
      }
    );
  };

  const handleCancelNotification = () => {
    setLoading(true);
    cancelNotifications(
      { notificationId: selectedNotification?._id },
      (success) => {
        if (success.isSuccess) {
          setLoading(false);
          handleSuccess(success?.message || "Success");
        }
      },
      (error) => {
        setLoading(false);
        toastSuccess("Failed");
      }
    );
  };

  let modalTitle = "Cancel Notification";
  if (mode === "View") {
    modalTitle = "View Notification";
  } else if (mode === "Edit") {
    modalTitle = "Edit Notification";
  } else if (mode === "Send") {
    modalTitle = "Send Notification";
  }

  let content = (
    <Form
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
      disabled={mode === "View"}
    >
      <FormField
        label="Notification Title (English)"
        name="titleEn"
        control={control}
        placeholder="Enter title in English"
        validateStatus={errors.titleEn ? "error" : ""}
        help={errors.titleEn?.message}
      />

      <FormField
        label="Notification Title (Arabic)"
        name="titleAr"
        control={control}
        placeholder="ادخل العنوان بالعربية"
        validateStatus={errors.titleAr ? "error" : ""}
        help={errors.titleAr?.message}
      />

      <FormField
        label="Notification Content (English)"
        name="messageEn"
        control={control}
        placeholder="Enter content in English"
        validateStatus={errors.messageEn ? "error" : ""}
        help={errors.messageEn?.message}
        inputType="textarea"
      />

      <FormField
        label="Notification Content (Arabic)"
        name="messageAr"
        control={control}
        placeholder="ادخل المحتوى بالعربية"
        validateStatus={errors.messageAr ? "error" : ""}
        help={errors.messageAr?.message}
        inputType="textarea"
      />

      <Form.Item
        label="Notification Recipients"
        validateStatus={errors.userType ? "error" : ""}
        help={errors.userType?.message}
      >
        <Controller
          name="userType"
          control={control}
          render={({ value, field }) => (
            <Select
              {...field}
              value={field.value}
              placeholder="Select Recipients"
            >
              <Option value="1">All users</Option>
              <Option value="2">Subscribed users</Option>
              <Option value="3">Unsubscribed users</Option>
            </Select>
          )}
        />
      </Form.Item>

      {!getValues("isSendNow") && (
        <Form.Item
          label="Scheduled date and time"
          validateStatus={errors.scheduleTime ? "error" : ""}
          help={errors.scheduleTime?.message}
        >
          <Controller
            name="scheduleTime"
            control={control}
            render={({ field }) => (
              <DatePicker
                style={{ width: "100%" }}
                size="middle"
                {...field}
                placeholder="Select Schedule Time"
                format="YYYY-MM-DD hh:mm A"
                showTime
                use12Hours
                // format="YYYY-MM-DDTHH:mm:ss.SSS[Z]"
                // format="DD-MM-YYYY"
                // moment(data.scheduleTime).format()
              />
            )}
          />
        </Form.Item>
      )}

      <Form.Item>
        <Controller
          name="isSendNow"
          control={control}
          render={({ field: { value, onChange } }) => {
            console.log("🚀 ~ value:", value);
            return (
              <Checkbox
                checked={value}
                onChange={(e) => {
                  onChange(e.target.checked);
                  setChecked((preState) => !preState);
                }}
              >
                Send Now
              </Checkbox>
            );
          }}
        />
      </Form.Item>

      <div className="flex justify-end gap-4">
        <SecondaryButton buttonName={"Cancel"} onClick={onCancel} />
        {mode !== "View" && (
          <PrimaryButton
            buttonName={"Send"}
            type="submit"
            loading={loading}
            disableMe={!isValid}
          />
        )}
      </div>
      <DevTool control={control} />
    </Form>
  );

  if (mode === "Cancel") {
    content = (
      <>
        <h6 className="text-center">
          Are you sure you want to cancel this scheduled notification?
        </h6>
        <div className="flex justify-center gap-4 mt-4">
          <SecondaryButton buttonName={"Cancel"} onClick={onCancel} />
          <Button
            className="primary-popup-button bg-danger"
            onClick={handleCancelNotification}
            loading={loading}
          >
            Confirm
          </Button>
        </div>
      </>
    );
  }

  return (
    <Modal open={open} title={modalTitle} onCancel={onCancel} footer={null}>
      {content}
    </Modal>
  );
}
